<script lang="ts">
	import type { Template, DomApi } from 'types/common'
	import type { Column } from '@isoftdata/svelte-table'
	import type { AdditionalInfoType } from './ItemDetail.svelte'
	type Inventories = PartSearchResultsData$result['inventories']
	import type { InventoryItem } from './results.ts'
	import type { Breakpoints } from '@isoftdata/utility-bootstrap'
	import type { AbstractStateRouter } from 'abstract-state-router'
	import type { PartSearchResultsData$result, PartSearchAppraisal$result } from '$houdini'
	import type { PerPageCount } from 'utility/search-fields'

	type AppraisalResult = PartSearchAppraisal$result['inventories']['items'][number]['appraisal']

	import { localWritable } from '@macfja/svelte-persistent-store'
	import { graphql } from '$houdini'
	import { tick, onMount } from 'svelte'
	import { slide } from 'svelte/transition'
	import Modal from '@isoftdata/svelte-modal'
	import ItemDetail from './ItemDetail.svelte'
	import Select from '@isoftdata/svelte-select'
	import Button from '@isoftdata/svelte-button'
	import Checkbox from '@isoftdata/svelte-checkbox'
	import { Table, Td } from '@isoftdata/svelte-table'
	import ImageThumbnail from '@isoftdata/svelte-image-thumbnail'
	import { getBootstrapBreakpoint } from '@isoftdata/utility-bootstrap'
	import { format as formatCurrency } from '@isoftdata/utility-currency'
	import { perPageCounts, computeTypeLabelColumns } from 'utility/search-fields'
	import ReportJobModal from 'components/ReportJobModal.svelte'
	import type { i18n } from 'types/common'
	import { getContext } from 'svelte'
	const { t: translate } = getContext<i18n>('i18next')

	interface Props {
		asr: AbstractStateRouter<Template, DomApi>
		sortColumnProp: string
		pageNumber: number
		inventories: Inventories
		sortDirection?: 'ASC' | 'DESC'
		perPageCount: PerPageCount
		selectedInventoryId: number | null
		previousSortDirection?: 'ASC' | 'DESC'
		inventoryTypeId: number | undefined
		showAppraisal: boolean
		appraisalResults: Map<number, AppraisalResult>
	}

	let {
		asr,
		sortColumnProp,
		pageNumber,
		inventories,
		sortDirection = 'ASC',
		perPageCount = $bindable(),
		selectedInventoryId,
		previousSortDirection = $bindable('DESC'),
		inventoryTypeId,
		showAppraisal,
		appraisalResults,
	}: Props = $props()

	const inventoryAllocationsQuery = graphql(`
		query PartSearchResultsAllocations($filter: InventoryFilter) {
			inventories(filter: $filter) {
				items {
					id
					allocations {
						salesOrderLines {
							items {
								id
							}
						}
						jobParts {
							id
						}
						transferOrderLines {
							id
						}
					}
				}
			}
		}
	`)

	const showThumbnails = localWritable('showThumbs', true)

	let itemDetailInventoryItem: InventoryItem | null = $state(null)
	let bootstrapBreakpoint: Breakpoints | 'xxl' = $state(getBootstrapBreakpoint()) //it can't actually be xxl class, but because of the typing of the getBootstrapBreakpoint fn, we need to allow xxl
	let showItemDetailModal: boolean = $state(false)
	let itemDetailAdditionalInfoType: AdditionalInfoType = $state('Basic Info')
	let inventoryTotalAllocationsMap: Map<number, number> = $state(new Map())
	let selectedRowIds: Array<number> = $derived(selectedInventoryId ? [selectedInventoryId] : [])
	let reportJobModal: ReportJobModal = $state()!

	const itemDetailManModSubtitle = $derived(computeItemDetailManModSubtitle(itemDetailInventoryItem))

	const isPhoneSize = $derived(bootstrapBreakpoint === 'xs')

	let typeLabelColumns = $derived(
		computeTypeLabelColumns(
			inventoryTypeId,
			inventories.items.map(item => item.inventoryType),
		),
	)

	const resultsColumns = $derived.by(() => {
		let columns: Column[] = [
			{ property: 'attentionIcons', name: '', icon: 'circle-info', sortType: false, minWidth: '150px' },
			{ property: 'storeId', name: translate('common:store', 'Store') },
			{ property: 'tagNumber', name: translate('common:tagNum', 'Tag #') },
			{ property: 'inventoryTypeName', name: translate('common:partType', 'Part Type') },
			{ property: 'manufacturerName', name: translate('common:partManufacturer', 'Part Manufacturer') },
			{ property: 'modelName', name: translate('common:partModel', 'Part Model') },
			{ property: 'category', name: translate('common:category', 'Category') },
			{ property: 'vehicleYear', name: translate('common:year', 'Year') },
			{ property: 'vehicleMake', name: translate('common:make', 'Make') },
			{ property: 'vehicleModel', name: translate('common:model', 'Model') },
			{ property: 'bodyStyle', name: translate('common:bodyStyle', 'Body Style') },
			{ property: 'side', name: translate('common:side', 'Side') },
			{ property: 'oemNumber', name: translate('common:oemNum', 'OEM #') },
			{ property: 'dateEntered', name: translate('customer.dateEntered', 'Date Entered') },
			{ property: 'status', name: translate('common:status', 'Status') },
			{ property: 'userStatus', name: translate('common:userStatus', 'User Status') },
			{ property: 'quantity', name: translate('common:qty', 'Qty') },
			{ property: 'wholesalePrice', name: translate('common:wholesale', 'Wholesale') },
			{ property: 'retailCorePrice', name: translate('common:core', 'Core') },
			{ property: 'retailPrice', name: translate('common:retail', 'Retail') },
			...typeLabelColumns,
		]

		if ($showThumbnails) {
			const attentionColumnIndex = columns.findIndex(column => (column.property = 'attentionIcons'))
			if (attentionColumnIndex > -1) {
				columns.splice(attentionColumnIndex + 1, 0, { property: 'imageThumbnail', icon: 'photo-video', name: '' })
			}
		}

		if (showAppraisal) {
			const quantityIndex = columns.findIndex(column => column.property === 'quantity')
			if (quantityIndex > -1) {
				columns.splice(quantityIndex + 1, 0, {
					property: 'appraisal',
					name: translate('common:appraisedValue', 'Appraised Value'),
					sortType: false,
					numeric: true,
				})
			}
		}

		return columns
	})

	const sortColumn: Column | undefined = $derived(
		sortColumnProp ? resultsColumns.find(column => column.property === sortColumnProp) : undefined,
	)

	function computeItemDetailManModSubtitle(item: InventoryItem | null): string {
		if (item) {
			const { manufacturer, model, vehicleMake, vehicleModel, vehicleYear } = item
			if (manufacturer && model) {
				return [manufacturer.name, model.name].filter(v => v).join(' > ')
			} else if (vehicleMake && vehicleModel) {
				return [vehicleYear, vehicleMake, vehicleModel].filter(v => v).join(' > ')
			}
		}

		return ''
	}

	async function showDetailPane(item: InventoryItem, additionalInfoType: AdditionalInfoType) {
		if (item.id === itemDetailInventoryItem?.id && additionalInfoType === itemDetailAdditionalInfoType) {
			//They clicked the same button, so just close the sidebar
			itemDetailInventoryItem = null
			additionalInfoType = 'Basic Info'
			return
		}

		itemDetailInventoryItem = item
		if (isPhoneSize) {
			showItemDetailModal = true
		}
		await tick()
		if (additionalInfoType) {
			itemDetailAdditionalInfoType = additionalInfoType
		}
	}

	onMount(async () => {
		if (inventories.items.length) {
			const allocationsResponse = await inventoryAllocationsQuery.fetch({
				variables: { filter: { ids: inventories.items.map(item => item.id) } },
			})
			if (allocationsResponse.data?.inventories.items) {
				for (const item of allocationsResponse.data?.inventories.items) {
					inventoryTotalAllocationsMap.set(
						item.id,
						item.allocations.jobParts.length +
							item.allocations.salesOrderLines.items.length +
							item.allocations.transferOrderLines.length,
					)
				}
				inventoryTotalAllocationsMap = inventoryTotalAllocationsMap
			} else {
				inventoryTotalAllocationsMap = new Map()
			}
		}
	})
</script>

<svelte:window
	onresize={() => {
		bootstrapBreakpoint = getBootstrapBreakpoint()
		if (!isPhoneSize && showItemDetailModal) {
			showItemDetailModal = false
		} else if (isPhoneSize && itemDetailInventoryItem) {
			showItemDetailModal = true
		}
	}}
/>

<div class="card-body">
	<div class="form-row justify-content-between align-items-end">
		<div class="col-auto">
			<Checkbox
				label={translate('common:showImageThumbnails', 'Show Image Thumbnails')}
				bind:checked={$showThumbnails}
				inline
			/>
		</div>
		<div class="col-auto">
			{inventories.totalItems}
			{translate('partSearch.inventoryItemsFound', 'Inventory Items Found')}
		</div>
		<div class="col-auto">
			<Select
				label={translate('common:resultsPerPage', 'Results Per Page')}
				showEmptyOption={false}
				bind:value={perPageCount}
				onchange={() => {
					localStorage.setItem('numPartRows', perPageCount)
					asr.go(null, { perPageCount, pageNumber: 1 }, { inherit: true })
				}}
			>
				{#each perPageCounts as perPageCount}
					<option value={perPageCount}>{perPageCount}</option>
				{/each}
			</Select>
		</div>
		{#if itemDetailInventoryItem && !isPhoneSize}
			<div class="col-md-6 col-lg-4 col-xl-3"></div>
		{/if}
	</div>

	<div class="form-row flex-nowrap">
		<div class={itemDetailInventoryItem && !isPhoneSize ? 'col-md-6 col-lg-8' : 'col-auto'}>
			<Table
				parentStyle="overflow-y: auto; max-height: 85vh;"
				class="table-sm border-0"
				responsive
				columns={resultsColumns}
				rows={inventories.items}
				currentPageNumber={pageNumber}
				totalItemsCount={inventories.totalItems}
				idProp="id"
				perPageCount={parseInt(perPageCount, 10)}
				stickyHeader
				columnHidingEnabled
				{previousSortDirection}
				{sortDirection}
				{sortColumn}
				bind:selectedRowIds
				pageChange={({ pageNumber }) => asr.go(null, { pageNumber: pageNumber }, { inherit: true })}
				columnClickedMethod={async (column, direction) => {
					previousSortDirection = sortDirection === 'ASC' ? 'DESC' : 'ASC'
					asr.go(null, { sortColumn: column.property, sortDirection: direction, pageNumber: 1 }, { inherit: true })
				}}
			>
				{#snippet children({ row: inventoryItem })}
					{@const hasPartManModel = inventoryItem.manufacturer?.name && inventoryItem.model?.name}
					<tr
						class="cursor-pointer"
						class:table-primary={selectedRowIds.includes(inventoryItem.id)}
						onclick={() => asr.go('app.part', { sku: inventoryItem.skuNumber, storeId: inventoryItem.storeId })}
					>
						<Td
							property="attentionIcons"
							stopPropagation
						>
							<input
								type="checkbox"
								style="height: 24px; width: 24px; vertical-align: middle;"
								bind:group={selectedRowIds}
								value={inventoryItem.id}
							/>
							<Button
								size="xs"
								color="dark"
								outline={!(
									itemDetailInventoryItem?.id === inventoryItem.id && itemDetailAdditionalInfoType === 'Basic Info'
								)}
								iconClass="circle-info"
								title={translate('partSearch.viewBasicItemDetails', 'View Basic Item Details')}
								onclick={() => {
									showDetailPane(inventoryItem, 'Basic Info')
								}}
							/>
							{#if inventoryItem.messageCount}
								<Button
									size="xs"
									color="dark"
									outline={!(
										itemDetailInventoryItem?.id === inventoryItem.id && itemDetailAdditionalInfoType === 'Messages'
									)}
									iconClass="message-lines"
									title={translate('partSearch.viewPartMessages', 'View Part Messages')}
									onclick={() => {
										showDetailPane(inventoryItem, 'Messages')
									}}>{inventoryItem.messageCount}</Button
								>
							{/if}
							<Button
								size="xs"
								color="dark"
								style="width: 46px;"
								outline={!(
									itemDetailInventoryItem?.id === inventoryItem.id && itemDetailAdditionalInfoType === 'Allocations'
								)}
								iconClass="files"
								title={inventoryTotalAllocationsMap.get(inventoryItem.id)
									? translate('partSearch.viewPartAllocations', 'View Part Allocations')
									: translate('partSearch.noAllocationsForThisPart', 'No Allocations for This Part')}
								colorGreyDisabled={false}
								disabled={$inventoryAllocationsQuery.fetching ||
									!inventoryTotalAllocationsMap.has(inventoryItem.id) ||
									!inventoryTotalAllocationsMap.get(inventoryItem.id)}
								onclick={() => {
									showDetailPane(inventoryItem, 'Allocations')
								}}
								>{$inventoryAllocationsQuery.fetching
									? ''
									: (inventoryTotalAllocationsMap.get(inventoryItem.id) ?? '0')}</Button
							>
							{#if inventoryItem.attachments.length}
								<Button
									size="xs"
									color="dark"
									outline={!(
										itemDetailInventoryItem?.id === inventoryItem.id && itemDetailAdditionalInfoType === 'Attachments'
									)}
									iconClass="paperclip"
									title={translate('vehicleSearch.viewPartAttachments', 'View Part Attachments')}
									onclick={() => {
										showDetailPane(inventoryItem, 'Attachments')
									}}>{inventoryItem.attachments.length}</Button
								>
							{/if}
							{#if inventoryItem.vehicle?.id}
								<Button
									size="xs"
									color="dark"
									outline={!(
										itemDetailInventoryItem?.id === inventoryItem.id && itemDetailAdditionalInfoType === 'Vehicle Info'
									)}
									iconClass="truck"
									title={translate('vehicleSearch.viewVehicleInfo', 'View Vehicle Info')}
									onclick={() => {
										showDetailPane(inventoryItem, 'Vehicle Info')
									}}
								/>
							{/if}
						</Td>
						{#if $showThumbnails}
							<Td
								property="imageThumbnail"
								stopPropagation
							>
								<ImageThumbnail
									thumbnailFile={{ path: inventoryItem?.attachments?.[0]?.file?.path || '' }}
									showThumbnail
									showImageCount
									noImagePath="images/noimage.jpg"
									context={inventoryItem.attachments}
									fileCount={inventoryItem.attachments.filter(attachment =>
										attachment.file.mimetype.startsWith('image/'),
									).length}
									click={() => showDetailPane(inventoryItem, 'Attachments')}
								/>
							</Td>
						{/if}
						<Td property="storeId">{inventoryItem.storeId}</Td>
						<Td property="tagNumber">{inventoryItem.tagNumber}</Td>
						<Td property="inventoryTypeName">{inventoryItem.inventoryType?.name ?? ''}</Td>
						<Td
							property="manufacturerName"
							title={hasPartManModel
								? translate('common:partManufacturer', 'Part Manufacturer')
								: inventoryItem.parentManufacturer
									? translate('partSearch.assemblyManufacturer', 'Assembly Manufacturer')
									: ''}
							>{hasPartManModel ? inventoryItem.manufacturer?.name : (inventoryItem.parentManufacturer?.name ?? '')}</Td
						>
						<Td
							property="modelName"
							title={hasPartManModel
								? translate('common:partModel', 'Part Model')
								: inventoryItem.parentModel
									? translate('partSearch.assemblyModel', 'Assembly Model')
									: ''}>{hasPartManModel ? inventoryItem.model?.name : (inventoryItem.parentModel?.name ?? '')}</Td
						>
						<Td property="category">{inventoryItem.category?.name ?? ''}</Td>
						<Td property="vehicleYear">{inventoryItem.vehicleYear}</Td>
						<Td property="vehicleMake">{inventoryItem.vehicleMake}</Td>
						<Td property="vehicleModel">{inventoryItem.vehicleModel}</Td>
						<Td property="bodyStyle">{inventoryItem.bodyStyle}</Td>
						<Td property="side">{inventoryItem.side}</Td>
						<Td property="oemNumber">{inventoryItem.oemNumber}</Td>
						<Td property="dateEntered"
							>{inventoryItem.dateEntered ? inventoryItem.dateEntered.toLocaleDateString() : ''}</Td
						>
						<Td property="status">{inventoryItem.status}</Td>
						<Td property="userStatus">{inventoryItem.userStatus}</Td>
						<Td property="quantity">{parseFloat(inventoryItem.quantity).toString()}</Td>
						{#if showAppraisal}
							<Td property="appraisal">
								<!-- Probably don't need to show $0 appraisals? -->
								{@const appraisal = appraisalResults.get(inventoryItem.id) ?? null}
								{#if appraisal?.value}
									<span
										title={`${translate('partSearch.min', 'Min')}: ${formatCurrency(appraisal?.min ?? 0)}\n${translate('partSearch.max', 'Max')}: ${formatCurrency(
											appraisal?.max ?? 0,
										)}\n${translate('partSearch.totalItems', 'Total Items')}: ${appraisal?.itemsAffected || 0}`}
										>{formatCurrency(appraisal?.value)}</span
									>
								{:else}
									<span class="text-muted">{translate('common:notSlashAcceptable', 'N/A')}</span>
								{/if}
							</Td>
						{/if}
						<Td property="wholesalePrice">{formatCurrency(inventoryItem.wholesalePrice)}</Td>
						<Td property="retailCorePrice">{formatCurrency(inventoryItem.retailCorePrice)}</Td>
						<Td property="retailPrice">{formatCurrency(inventoryItem.retailPrice)}</Td>
						{#each typeLabelColumns as column}
							<Td property={column.property}>
								{#if !inventoryTypeId && inventoryItem[column.property]?.data}
									<span
										class="font-weight-bold"
										style="white-space: nowrap;"
										>{inventoryItem[column.property.replace('Label', 'Field')]?.label ?? ''}:</span
									>
								{/if}
								{inventoryItem[column.property]?.data ?? ''}
							</Td>
						{/each}
					</tr>
				{/snippet}
				{#snippet noRows({ visibleColumnsCount })}
					<tr>
						<td colspan={visibleColumnsCount}
							>{translate('partSearch.NoMatchingPartsFound', ' No matching parts found.')}</td
						>
					</tr>
				{/snippet}
			</Table>
		</div>
		{#if itemDetailInventoryItem && !isPhoneSize}
			<div class="col-md-6 col-lg-4">
				<div
					transition:slide={{ axis: 'x', duration: 200 }}
					class="card"
				>
					<div class="card-header">
						<div class="d-flex justify-content-between align-items-center flex-nowrap">
							<h5 class="w-100 text-truncate">
								{#if itemDetailInventoryItem}
									{translate('common:tagNum', 'Tag #')}: {itemDetailInventoryItem?.tagNumber ?? ''}
									<br />
									<small>
										<span class="font-weight-bold">{itemDetailInventoryItem?.inventoryType?.name}</span>
										{itemDetailManModSubtitle}
									</small>
								{/if}
							</h5>
							<Button
								size="sm"
								iconClass="caret-right"
								outline
								title={translate('vehicleSearch.collapseItemDetailsPane', 'Collapse Item Details Pane')}
								onclick={() => (itemDetailInventoryItem = null)}
							/>
						</div>
					</div>
					<div
						class="card-body"
						style="overflow-y: auto; max-height: 80vh;"
					>
						<ItemDetail
							item={itemDetailInventoryItem}
							bind:additionalInfoType={itemDetailAdditionalInfoType}
							{asr}
							on:imageThumbnailClick={e => {
								const inventoryItem = inventories.items.find(item => item.id === e.detail)
								if (inventoryItem) {
									showDetailPane(inventoryItem, 'Attachments')
								}
							}}
						/>
					</div>
				</div>
			</div>
		{/if}
	</div>
</div>
<div class="card-footer">
	<Button
		outline
		size="sm"
		icon="dollar"
		href={asr.makePath(
			null,
			{
				showAppraisal: true,
			},
			{ inherit: true },
		)}>{translate('common:appraiseItems', 'Appraise Items')}</Button
	>
	<Button
		outline
		size="sm"
		icon="print"
		disabled={!selectedRowIds.length}
		onclick={() => {
			const selects = inventories.items
				.filter(item => selectedRowIds.includes(item.id))
				.map(
					item =>
						`SELECT ${item.skuNumber} AS inventoryid, ${item.storeId} AS storeid, NULL AS inventoryserialid, ${item.locations[0].name} AS location, ${item.quantity} AS quantity`,
				)

			const subselect = '('.concat(selects.join(' UNION ALL ').concat(') AS `tuples`'))

			console.log(subselect)
			reportJobModal.open({
				type: 'Tag',
				name: 'Tag',
				parameters: {
					subselect,
				},
			})
		}}>{translate('common:print', 'Print')} {selectedRowIds.length || ''} {translate('partSearch.tags', 'Tags')}</Button
	>
</div>
<Modal
	title={itemDetailInventoryItem?.inventoryType?.name ?? translate('partSearch.itemDetails', 'Item Details')}
	subtitle={itemDetailManModSubtitle}
	confirmShown={false}
	show={!!(itemDetailInventoryItem && showItemDetailModal)}
	close={() => {
		showItemDetailModal = false
		itemDetailInventoryItem = null
	}}
	>{#if itemDetailInventoryItem}
		<ItemDetail
			item={itemDetailInventoryItem}
			bind:additionalInfoType={itemDetailAdditionalInfoType}
			{asr}
		/>
	{/if}</Modal
>

<ReportJobModal bind:this={reportJobModal} />
